import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  LinkButton,
  ProblemError,
  SearchField,
  showToast,
  ToggleChip,
  useActionKey,
} from "@emdur/ui";
import { useRef, useState } from "react";
import { useApi } from "../lib/apiContext";
import {
  type CapabilityItem,
  type Client,
  permissionSummary,
  updateClient,
} from "../lib/clients";
import { errorText } from "../lib/errors";
import { proposalHref } from "../lib/proposals";
import { PermissionReview, RiskBadge } from "./Permissions";
import { useRegistry } from "./RegistryProvider";
import { permissionTitle } from "../lib/permissionCopy";
import { PermissionRefreshAction } from "./PermissionRefreshAction";
import {
  diffPermissions,
  requireAssignable,
  updateRole,
  type Role,
} from "../lib/roles";

type PickerTarget =
  | {
      client: Client;
      role?: never;
      org?: never;
      onSaved: (client: Client, etag?: string | null) => void;
      onPreview?: (client: Client) => void;
    }
  | {
      role: Role;
      org: string;
      client?: never;
      onSaved: (role: Role, etag?: string | null) => void;
      onPreview?: never;
    };
export function PermissionPicker(
  props: PickerTarget & {
    etag: string;
    catalogue: CapabilityItem[];
    isOpen: boolean;
    onClose: () => void;
    onReload: () => Promise<void>;
  },
) {
  const {
    client,
    role,
    etag,
    catalogue,
    isOpen,
    onClose,
    onReload,
    onPreview,
  } = props;
  const api = useApi();
  const keys = useActionKey();
  const registry = useRegistry();
  const before = useRef(client ?? role!).current;
  const name = role?.name ?? client!.client_name;
  const subject = role ? "role" : "app";
  const org = role ? props.org : client!.organization_id;
  const reasons = client?.["x-emdur-reasons"];
  const [selected, setSelected] = useState(before.capabilities);
  const [search, setSearch] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [proposal, setProposal] = useState("");
  const diff = diffPermissions(before.capabilities, selected);
  const shown = catalogue.filter(
    (item) =>
      (!role || item.assignable === true) &&
      (!onlySelected || selected.includes(item.capability)) &&
      `${permissionTitle(item.title)} ${item.group}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const groups = [...new Set(shown.map((item) => item.group))].sort();
  async function save() {
    if (keys.sending.current) return;
    keys.sending.current = true;
    setBusy(true);
    setError("");
    const patch = { capabilities: [...selected].sort() };
    const key = keys.keyFor("permissions", patch);
    const narrowing = diff.added.length === 0 && diff.removed.length > 0;
    if (narrowing && client)
      onPreview?.({
        ...client,
        capabilities: patch.capabilities,
        scopes: patch.capabilities,
        scope: patch.capabilities.join(" "),
      });
    try {
      if (role) requireAssignable(patch.capabilities, catalogue);
      const result = role
        ? await updateRole(api.fetch, org, role.key, patch, etag, key)
        : await updateClient(api.fetch, org, client!.id, patch, etag, key);
      keys.spent("permissions", key);
      void registry.reload();
      if (result.kind === "proposal") {
        if (narrowing && client) onPreview?.(client);
        setProposal(proposalHref(org, result.proposal.id));
        return;
      }
      if (props.role && result.kind === "role")
        props.onSaved(result.role, result.etag);
      else if (props.client && result.kind === "client")
        props.onSaved(result.client, result.etag);
      showToast({ title: "Permissions saved", tone: "positive" });
      onClose();
    } catch (failure) {
      if (narrowing && client) onPreview?.(client);
      const conflict =
        failure instanceof ProblemError && failure.problem.status === 412;
      setStale(conflict);
      setError(
        conflict
          ? `This ${subject} changed elsewhere. Reload it and review your changes again.`
          : errorText(failure),
      );
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }
  return (
    <Drawer
      title="Change permissions"
      isOpen={isOpen}
      onOpenChange={(open) => !open && !busy && onClose()}
    >
      <div className="permission-drawer flex min-h-full flex-col gap-5">
        {proposal ? (
          <Alert tone="positive" title="Sent for approval">
            <LinkButton href={proposal}>Open proposal</LinkButton>
          </Alert>
        ) : (
          <>
            <p
              role="status"
              className="permission-summary sticky top-0 z-10 bg-surface py-3 text-sm font-strong"
            >
              {permissionSummary(diff, catalogue)}
            </p>
            {reviewing ? (
              <PermissionReview
                name={name}
                diff={diff}
                catalogue={catalogue}
                reasons={reasons}
                subject={subject}
              />
            ) : (
              <>
                <SearchField
                  label="Search permissions"
                  value={search}
                  onChange={setSearch}
                />
                <ToggleChip
                  label="Selected only"
                  isSelected={onlySelected}
                  onChange={setOnlySelected}
                />
                {groups.length === 0 && (
                  <EmptyState title="No matching permissions">
                    Try a shorter search or turn off Selected only.
                  </EmptyState>
                )}
                {groups.map((group) => (
                  <fieldset key={group} className="flex flex-col gap-4">
                    <legend className="mb-3 text-sm font-strong text-muted">
                      {group}
                    </legend>
                    {shown
                      .filter((item) => item.group === group)
                      .map((item) => (
                        <Checkbox
                          key={item.capability}
                          label={
                            <span className="inline-flex flex-wrap items-center gap-2">
                              {permissionTitle(item.title)}
                              <RiskBadge risk={item.risk} />
                            </span>
                          }
                          description={
                            reasons?.[item.capability]?.trim() ||
                            (role
                              ? item.description
                              : "This app gave no reason for this permission.")
                          }
                          isSelected={selected.includes(item.capability)}
                          onChange={(checked) =>
                            setSelected((before) =>
                              checked
                                ? [...before, item.capability]
                                : before.filter(
                                    (capability) =>
                                      capability !== item.capability,
                                  ),
                            )
                          }
                        />
                      ))}
                  </fieldset>
                ))}
              </>
            )}
            {!selected.length && (
              <Alert tone="warning">
                {role
                  ? "A role needs at least one permission. Delete the role when it is no longer needed."
                  : "An app needs at least one permission. Revoke access to remove all permissions."}
              </Alert>
            )}
            {error && (
              <Alert tone="danger">
                {error}
                <PermissionRefreshAction when={error} />
                {stale && (
                  <div className="mt-3">
                    <Button
                      onPress={async () => {
                        await onReload();
                        onClose();
                      }}
                    >
                      Reload {subject}
                    </Button>
                  </div>
                )}
              </Alert>
            )}
            <div className="permission-actions sticky bottom-0 z-10 mt-auto flex flex-wrap gap-2 border-t border-line bg-surface py-4">
              {reviewing ? (
                <>
                  <Button
                    variant="primary"
                    isLoading={busy}
                    isDisabled={stale || !selected.length}
                    onPress={() => void save()}
                  >
                    Save permissions
                  </Button>
                  <Button isDisabled={busy} onPress={() => setReviewing(false)}>
                    Back to permissions
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  isDisabled={
                    !selected.length ||
                    (!diff.added.length && !diff.removed.length)
                  }
                  onPress={() => setReviewing(true)}
                >
                  Review changes
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
